import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connectToStores} from 'fluxible-addons-react';
import {
  saveEmployeeProfile,
  employeeCancelClicked,
  loadBranches,
  searchEmployeeConflicts,
  employeeValidationError,
  selectEmployee
} from '../actions/SearchActionCreators';
import Form from 'react-form-controlled';
import Validate from 'react-form-controlled-validate';
import {EmployeeShape, BranchShape, FormErrorShape} from '../../common/react/Shapes';
import MessageDefinitions from '../i18n/MessageDefinitions';
import {FormattedMessage, intlShape} from 'react-intl';
import {AsYouTypeFormatter} from 'google-libphonenumber/dist/libphonenumber';
import BranchResult from './BranchResult';
import debounce from 'lodash.debounce';
import FontAwesome from 'react-fontawesome';
import merge from 'lodash.merge';
import FormError from '../../common/util/FormError';
import {textInput} from '../../common/util/FormInput';
import EmployeeResult from './EmployeeResult';

const formSchema = {
  type: 'object',
  properties: {
    firstName: {
      type: 'string',
      minLength: 1
    },
    lastName: {
      type: 'string',
      minLength: 1
    },
    email: {
      type: 'string',
      format: 'email'
    },
    phone: {
      type: 'string',
      'pattern': '^\\([0-9]{3}\\)\\s*[0-9]{3}-[0-9]{4}$|^[0-9]{10}$'
    },
    nmlsId: {
      type: 'string',
      'pattern': '^[0-9]{0,12}$'
    }
  },
  'required': ['firstName', 'lastName', 'email', 'phone']
};

const requireEmployeeId = !window.ignite.ignoreEmployeeId;

if (requireEmployeeId) {
  formSchema.required.push('employeeId');
  if (window.ignite.validations && window.ignite.validations.employeeLogin) {
    merge(formSchema.properties, window.ignite.validations.employeeLogin);
  }
}

const requireNmlsId = window.ignite.validations && window.ignite.validations.requireEmployeeNmlsId;
if (requireNmlsId) {
  formSchema.required.push('nmlsId');
}

function branchesContains(branches, item) {
  return branches.find(branch => {
    return branch.id === item.id;
  });
}

@connectToStores(['LoginStore'], (context, props) => {
  const LoginStore = context.getStore('LoginStore');

  return {
    employee: LoginStore.getSelectedEmployee(),
    foundBranches: LoginStore.getBranches(),
    formErrors: LoginStore.getEmployeeErrors(),
    processing: LoginStore.isProcessing(),
    allowEdit: LoginStore.canEdit(),
    employeeConflicts: LoginStore.getEmployeeConflicts()
  };
})
class Edit extends Component {
  static propTypes = {
    employee: EmployeeShape,
    foundBranches: PropTypes.arrayOf(BranchShape),
    formErrors: PropTypes.arrayOf(FormErrorShape),
    processing: PropTypes.bool.isRequired,
    employeeConflicts: PropTypes.array
  };

  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
    intl: intlShape
  };

  constructor(props) {
    super(props);
    const {id, employeeId, firstName, lastName, email, phone, branches = [], nmlsId} = this.props.employee || {};
    this.state = {
      branches,
      form: {
        firstName,
        lastName,
        email,
        phone,
        employeeId,
        nmlsId,
        searchValue: ''
      }
    };
    this.searchValue = '';
    this.loadBranches = debounce(searchValue => {
      this.context.executeAction(loadBranches, {
        searchValue
      });
    }, 500, {
      maxWait: 1000
    });
    this.searchForEmployeeConflict = debounce(() => {
      this.context.executeAction(searchEmployeeConflicts, {
        searchByEmail: this.state.form.email,
        searchById: this.state.form.employeeId
      });
    }, 500);
  }

  componentDidMount() {
    this.form.validate(this.state.form, (_, formErrors = []) => {
      this.context.executeAction(employeeValidationError, formErrors);
    });
  }

  formValueChanged = state => {
    state.phone = state.phone.replace(/\D/g, '');
    this.form.validate(state).then((formErrors = []) => {
      this.context.executeAction(employeeValidationError, formErrors);
    });

    if(state.email !== this.state.form.email || state.employeeId !== this.state.form.employeeId){
      this.searchForEmployeeConflict();
    }

    this.setState({
      ...this.state,
      form: state
    });
  };

  branchSearchValueChanged = event => {
    this.searchValue = event.target.value;
    this.loadBranches(this.searchValue);
  };

  formSubmitted = state => {
    state.phone = state.phone.replace(/\D/g, '');
    const employee = state;
    employee.id = this.props.employee ? this.props.employee.id : null;
    employee.branches = this.state.branches;
    this.context.executeAction(saveEmployeeProfile, {
      employee
    });
  };

  cancelClicked = event => {
    event.preventDefault();
    this.context.executeAction(employeeCancelClicked, {});
  };

  render() {
    const formatMessage = this.context.intl.formatMessage;
    const {
      branches = [],
      form = {}
    } = this.state;
    const value = {
      ...form
    };

    const {foundBranches = [], formErrors = [], processing, allowEdit, employeeConflicts = []} = this.props;

    const cancelButton = this.props.employee
      ? <button onClick={this.cancelClicked} className="btn btn-cancel cancel" disabled={branches.length === 0}>
        <FormattedMessage {...MessageDefinitions.cancel} /></button>
      : null;

    const conflictResults = employeeConflicts.map(item => {
        const employeeClicked = () => {
          this.context.executeAction(selectEmployee, item);
        };
        return <div className="employee-login-result" onClick={employeeClicked} key={item.id}>
          <div className="name"><span className="firstName">{item.firstName}</span> <span className="lastName">{item.lastName}</span></div>
        </div>;
      });

    const conflictMessage = conflictResults && conflictResults.length > 0
      ? <div className="text-center">
          <FormattedMessage {...MessageDefinitions.profileConflicts}/>
        </div>
      : null;

    const branchResults = foundBranches.map(branch => {
      const addBranch = () => {
        if (!branchesContains(branches, branch)) {
          branches.push(branch);
          this.setState({
            ...this.state,
            branches
          });
        }
      };

      return <BranchResult key={branch.id} onClick={addBranch} branch={branch}>
        <FontAwesome name="plus-circle"/>
      </BranchResult>;
    });

    const employeeBranches = branches.map(branch => {
      const removeBranch = () => {
        const newBranches = branches.filter(b => {
          return branch.id !== b.id;
        });
        this.setState({
          ...this.state,
          branches: newBranches
        });
      };

      return <BranchResult key={branch.id} onClick={removeBranch} branch={branch}>
        <FontAwesome name="minus-circle"/>
      </BranchResult>;
    });

    const errorMessages = {};
    const fieldErrors = {};
    const fields = {};

    if (employeeBranches.length === 0) {
      errorMessages.employeeBranches = <FormError message={MessageDefinitions.employeeBranchesError}/>;
    }
    formErrors.forEach(error => {
      fieldErrors[error.key] = true;
    });

    ['firstName', 'lastName', 'employeeId', 'email', 'phone', 'nmlsId'].forEach(key => {
      let errorMessage = MessageDefinitions[`${key}Error`];
      let hasError = fieldErrors[key];
      if (key === 'email' && employeeConflicts.find(e => e.email === value.email)) {
        hasError = true;
        errorMessage = MessageDefinitions.emailConflicts;
      } else if (key === 'employeeId' && employeeConflicts.find(e => e.employeeId === value.employeeId)) {
        hasError = true;
        errorMessage = MessageDefinitions.employeeIdConflicts;
      }
      fields[key] = textInput({
        type: 'text',
        name: key,
        labelMessage: MessageDefinitions[key],
        placeholderMessage: MessageDefinitions[`${key}Placeholder`],
        errorMessage,
        hasError,
        formatMessage,
        disabled: !allowEdit
      })
    });

    const formValid = formErrors.length === 0 && employeeBranches.length > 0;
    const formatter = new AsYouTypeFormatter('US');
    let phoneNumber = '';
    for (const n of value.phone || []) {
      phoneNumber = formatter.inputDigit(n);
    }
    value.phone = phoneNumber;
    value.searchValue = this.searchValue;

    const buttonMessage =
      processing
        ? <FontAwesome name="refresh" spin/>
        : <FormattedMessage {...MessageDefinitions.save} />;

    const employeeIdSection = requireEmployeeId ? fields.employeeId : null;
    const nmlsIdSection = requireNmlsId ? fields.nmlsId : null;

    return <div className="employee-login-edit">
      <Validate schema={formSchema} ref={f => this.form = f}>
        <Form value={value}
              onChange={this.formValueChanged}
              onSubmit={this.formSubmitted}>
          <div className="form-body">
            {fields.firstName}
            {fields.lastName}
            {employeeIdSection}
            {nmlsIdSection}
            {fields.email}
            {fields.phone}
            <div className="form-group branch-search">
              <label className="form-label">
                <FormattedMessage {...MessageDefinitions.branchesSearch} />
              </label>
              <input type="text"
                     className="form-control"
                     name="searchValue"
                     placeholder={formatMessage(MessageDefinitions.branchesSearchPlaceholder)}
                     onChange={this.branchSearchValueChanged} />
            </div>
            <div className="form-group branch-result">
              {branchResults}
            </div>
            <div className="form-group employee-branches">
              <label className="form-label">
                <FormattedMessage {...MessageDefinitions.branchesAssociated} />
              </label>
              <div className="form-control">
                {employeeBranches}
              </div>
              {errorMessages.employeeBranches}
            </div>
          </div>
          <div>
            {conflictMessage}
            {conflictResults}
          </div>
          <div className="form-actions">
            {cancelButton}
            <button type="submit" className="btn btn-primary save" disabled={!formValid || processing || (conflictResults && conflictResults.length > 0)}>
              {buttonMessage}
            </button>
          </div>
        </Form>
      </Validate>
    </div>;
  }
}

export default Edit;



/** WEBPACK FOOTER **
 ** ./src/main/js/employeeLogin/components/Edit.js
 **/